import "../../../styles/log.css";
import { Grid, html } from "gridjs";
import "gridjs/dist/theme/mermaid.css";
import Toastify from "toastify-js";
import "toastify-js/src/toastify.css";
import { uiState } from "../../state/uiState.js";
import { truncateLabel } from "../../utils/textPresentation.js";

const API = "/api";

function showToast(message, type = "success") {
    const background = type === "success" ? "#42525e" : "#ee4447";
    Toastify({
        text: message,
        duration: 2800,
        close: true,
        gravity: "top",
        position: "right",
        stopOnFocus: true,
        style: {
            background,
            borderRadius: "8px",
            fontSize: "13px",
        },
    }).showToast();
}

// ── Badge & label tanımları ──────────────────────────────────────────────────

const ISLEM_BADGE = {
    olusturma:        { bg: "#dcfce7", color: "#16a34a", border: "#86efac", label: "Oluşturma" },
    duzenleme:        { bg: "#dbeafe", color: "#1d4ed8", border: "#93c5fd", label: "Düzenleme" },
    silme:            { bg: "#fee2e2", color: "#dc2626", border: "#fca5a5", label: "Silme" },
    durum_degisikligi:{ bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db", label: "Durum Değişikliği" },
    yeni_sifre:       { bg: "#fef3c7", color: "#b45309", border: "#fcd34d", label: "Yeni Şifre" },
};

const MODUL_LABEL = {
    demirbas: "Demirbaş",
    tanim:    "Tanım",
    kisi:     "Kişi",
    uye:      "Üye",
};

function formatCurrencyTotals(raw) {
    if (!raw || typeof raw !== "object") return null;
    const entries = Object.entries(raw).filter(([, amount]) => amount !== null && amount !== undefined && amount !== "");
    if (!entries.length) return null;
    const symbols = { TRY: "₺", EUR: "€", USD: "$" };
    return entries
        .map(([currencyCode, amount]) => {
            const symbol = symbols[currencyCode] || currencyCode;
            const formatted = Number(amount).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            return `${currencyCode}: ${formatted} ${symbol}`;
        })
        .join(" | ");
}

const ISLEM_BANNER = {
    olusturma:        { bg: "#f0fdf4", color: "#16a34a", border: "#86efac", icon: "check_circle",  text: "Yeni kayıt oluşturuldu" },
    duzenleme:        { bg: "#eff6ff", color: "#1d4ed8", border: "#93c5fd", icon: "info",           text: "Kayıt düzenlendi — işlem anındaki hal" },
    silme:            { bg: "#fff5f5", color: "#dc2626", border: "#fca5a5", icon: "warning",        text: "Silinmiş kayıt — arşiv görüntüleniyor" },
    durum_degisikligi:{ bg: "#f9fafb", color: "#6b7280", border: "#d1d5db", icon: "swap_horiz",     text: "Durum değişikliği" },
    yeni_sifre:       { bg: "#fefce8", color: "#b45309", border: "#fcd34d", icon: "lock_reset",     text: "Şifre değiştirildi — detay bilgisi yok" },
};

// ── Modül bazlı alan tanımları ───────────────────────────────────────────────

const DEMIRBAS_FIELDS = [
    { key: "demirbas_id",    label: "Demirbaş ID",    fmt: (v) => {
        if (v === null || v === undefined || v === "") return null;
        const value = String(v);
        return value.startsWith("DMB") ? value : "DMB" + value.padStart(8, "0");
    } },
    { key: "departman",      label: "Departman" },
    { key: "urun_grubu",     label: "Ürün Grubu" },
    { key: "urun_alt_grubu", label: "Ürün Alt Grubu" },
    { key: "urun_cinsi",     label: "Ürün Cinsi" },
    { combined: ["marka", "model"], label: "Marka / Model", fmt: (s) => [s.marka, s.model].filter(Boolean).join(" / ") || null },
    { key: "seri_numarasi",  label: "Seri No" },
    { key: "aciklama",       label: "Açıklama" },
    { key: "urun_durumu",    label: "Ürün Durumu" },
    { key: "zimmet_durumu",  label: "Zimmet Durumu" },
    { key: "zimmetlenen_kisi", label: "Zimmetlenen Kişi", aliases: ["zimmetlened_kisi"] },
    { key: "zimmetlenen_tc", label: "Zimmetlenen TC", aliases: ["zimmetlened_tc"] },
    { key: "zimmet_tarihi",  label: "Zimmet Tarihi" },
    { key: "lokasyon",       label: "Lokasyon" },
    { key: "oda",            label: "Oda" },
    { key: "yer",            label: "Yer" },
    { key: "fatura_sayisi",  label: "Fatura Sayısı" },
    { key: "toplam_satin_alma_degeri", label: "Toplam Satın Alma Değeri",
      fmt: (v) => v != null && v !== "" ? Number(v).toLocaleString("tr-TR", { minimumFractionDigits: 2 }) + " ₺" : null },
    { key: "fatura_toplamlari", label: "Fatura Toplamları", fmt: formatCurrencyTotals },
];

const KISI_FIELDS = [
    { key: "tc",         label: "T.C. Kimlik No" },
    { key: "first_name", label: "Ad" },
    { key: "last_name",  label: "Soyad" },
    { key: "departman",  label: "Departman" },
    { key: "unvan",      label: "Ünvan" },
    { key: "is_active",  label: "Durum", fmt: (v) => (v === 1 || v === true) ? "Aktif" : "Pasif" },
];

const UYE_FIELDS = [
    { key: "email",      label: "E-posta" },
    { key: "yetki",      label: "Yetki" },
    { combined: ["first_name", "last_name"], label: "Ad Soyad", fmt: (s) => [s.first_name, s.last_name].filter(Boolean).join(" ") || null },
    { key: "kisi_tc",    label: "T.C. Kimlik No" },
    { key: "departman",  label: "Departman" },
    { key: "unvan",      label: "Ünvan" },
    { key: "is_active",  label: "Üyelik Durumu", fmt: (v) => (v === 1 || v === true) ? "Aktif" : "Pasif" },
    { key: "kisi_is_active", label: "Kişi Durumu", fmt: (v) => (v === 1 || v === true) ? "Aktif" : "Pasif" },
];

const TANIM_FIELDS = [
    { key: "name",        label: "Ad" },
    { key: "tablo_label", label: "Tür" },
];

const MODULE_FIELDS = { demirbas: DEMIRBAS_FIELDS, kisi: KISI_FIELDS, uye: UYE_FIELDS, tanim: TANIM_FIELDS };
const MODULE_SECTION = { demirbas: "Demirbaş Bilgileri", kisi: "Kişi Bilgileri", uye: "Üye Bilgileri", tanim: "Tanım Bilgileri" };
const MODUL_SORT_ORDER = { demirbas: 1, tanim: 2, kisi: 3, uye: 4 };
const ISLEM_SORT_ORDER = { olusturma: 1, duzenleme: 2, silme: 3, durum_degisikligi: 4, yeni_sifre: 5 };

// ── Yardımcı ────────────────────────────────────────────────────────────────

function showFilterOverlay(container) {
    let ov = container.querySelector(".filter-loading-overlay");
    if (!ov) {
        ov = document.createElement("div");
        ov.className = "filter-loading-overlay";
        ov.innerHTML = `<div class="filter-loading-spinner"></div><span class="filter-loading-text">Yükleniyor...</span>`;
        container.appendChild(ov);
    }
    ov.classList.remove("hidden");
}

function hideFilterOverlay(container) {
    container.querySelector(".filter-loading-overlay")?.classList.add("hidden");
}

async function withFilterOverlay(container, fn) {
    showFilterOverlay(container);
    try { await fn(); }
    finally {
        await new Promise((resolve) => setTimeout(resolve, 300));
        hideFilterOverlay(container);
    }
}

function esc(str) {
    return String(str ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function extractFilenameFromContentDisposition(headerValue) {
    if (!headerValue) return "";
    const utfMatch = headerValue.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfMatch?.[1]) {
        try { return decodeURIComponent(utfMatch[1]); }
        catch { return utfMatch[1]; }
    }
    const basicMatch = headerValue.match(/filename="?([^";]+)"?/i);
    return basicMatch?.[1] || "";
}

async function downloadDemirbasCsvExport(button) {
    if (!button || button.disabled) return;

    const originalHtml = button.innerHTML;
    button.disabled = true;
    button.innerHTML = `<span class="material-icons">hourglass_top</span><span>CSV hazirlaniyor...</span>`;

    try {
        const response = await fetch(`${API}/demirbas/export/csv`, {
            method: "GET",
            credentials: "same-origin",
        });

        if (!response.ok) {
            let errorMessage = `HTTP ${response.status}`;
            try {
                const data = await response.json();
                errorMessage = data?.error || data?.message || errorMessage;
            } catch { /* no-op */ }
            throw new Error(errorMessage);
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const fileName =
            extractFilenameFromContentDisposition(response.headers.get("content-disposition")) ||
            "demirbas-envanter.csv";

        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 0);

        showToast("Demirbas CSV export hazirlandi.", "success");
    } catch (err) {
        showToast(err?.message || "Demirbas CSV export olusturulamadi.", "error");
    } finally {
        button.disabled = false;
        button.innerHTML = originalHtml;
    }
}

function ellipsisCellHtml(value, className = "") {
    const text = value === null || value === undefined || value === "" ? "—" : String(value);
    const safe = esc(text);
    const cls = ["grid-cell-ellipsis", className].filter(Boolean).join(" ");
    return html(`<span class="${cls}" title="${safe}">${safe}</span>`);
}

function islemBadgeHtml(islem) {
    const cfg = ISLEM_BADGE[islem];
    if (!cfg) return esc(islem ?? "—");
    return `<span class="log-badge" title="${esc(cfg.label)}" style="background:${cfg.bg};color:${cfg.color};border-color:${cfg.border}">${cfg.label}</span>`;
}

function modulBadgeHtml(modul) {
    const label = MODUL_LABEL[modul] ?? modul ?? "—";
    return `<span class="log-badge log-badge--modul" title="${esc(label)}">${esc(label)}</span>`;
}

function buildLogUrl() {
    const params = new URLSearchParams();
    const { modul, islem, yapan_tc, tarih_baslangic, tarih_bitis } = uiState.log.filters;
    if (modul) params.append("modul", modul);
    if (islem) params.append("islem", islem);
    if (yapan_tc) params.append("yapan_tc", yapan_tc);
    if (tarih_baslangic) params.append("tarih_baslangic", tarih_baslangic);
    if (tarih_bitis) params.append("tarih_bitis", tarih_bitis);
    const qs = params.toString();
    return qs ? `${API}/islem-kayitlari?${qs}` : `${API}/islem-kayitlari`;
}

function buildLogQueryString() {
    const params = new URLSearchParams();
    const { modul, islem, yapan_tc, tarih_baslangic, tarih_bitis } = uiState.log.filters;
    if (modul) params.append("modul", modul);
    if (islem) params.append("islem", islem);
    if (yapan_tc) params.append("yapan_tc", yapan_tc);
    if (tarih_baslangic) params.append("tarih_baslangic", tarih_baslangic);
    if (tarih_bitis) params.append("tarih_bitis", tarih_bitis);
    return params.toString();
}

function syncLogFilterBadge() {
    const badge = document.querySelector("#log-filter-section .filter-aktif-badge");
    if (!badge) return;
    badge.hidden = !buildLogQueryString();
}

function compareText(a, b) {
    return String(a ?? "").localeCompare(String(b ?? ""), "tr", { sensitivity: "base" });
}

function compareNumber(a, b) {
    return Number(a ?? 0) - Number(b ?? 0);
}

function parseTrDateTime(value) {
    const text = String(value ?? "").trim();
    const match = text.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})$/);
    if (!match) return 0;
    const [, day, month, year, hour, minute, second] = match;
    return new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second)
    ).getTime();
}

function compareModul(a, b) {
    const left = MODUL_SORT_ORDER[String(a ?? "").trim()] ?? Number.MAX_SAFE_INTEGER;
    const right = MODUL_SORT_ORDER[String(b ?? "").trim()] ?? Number.MAX_SAFE_INTEGER;
    return left - right || compareText(a, b);
}

function compareIslem(a, b) {
    const left = ISLEM_SORT_ORDER[String(a ?? "").trim()] ?? Number.MAX_SAFE_INTEGER;
    const right = ISLEM_SORT_ORDER[String(b ?? "").trim()] ?? Number.MAX_SAFE_INTEGER;
    return left - right || compareText(a, b);
}

// ── Grid ────────────────────────────────────────────────────────────────────

let logGrid = null;
let logDidBound = false;

function renderLogGrid(url = buildLogUrl()) {
    const tableGrid = document.querySelector(".log-table-grid");
    if (!tableGrid) return;

    uiState.log.lastSearchUrl = url;

    if (logGrid) { logGrid.destroy(); logGrid = null; }
    tableGrid.innerHTML = "";

    logGrid = new Grid({
        columns: [
            {
                id: "olusturma_tarihi",
                name: html('<div style="margin-top:3px;">Tarih</div>'),
                minWidth: "155px",
                sort: {
                    compare: (a, b) => compareNumber(parseTrDateTime(a), parseTrDateTime(b)),
                },
                formatter: (cell) => ellipsisCellHtml(cell),
            },
            {
                id: "yapan",
                name: html('<div style="margin-top:3px;">İşlemi Gerçekleştiren</div>'),
                minWidth: "150px",
                sort: {
                    compare: (a, b) => compareText(a, b),
                },
                formatter: (cell) => ellipsisCellHtml(cell, "grid-cell-ellipsis--xwide"),
            },
            {
                id: "modul",
                name: html('<div style="margin-top:3px;">Modül</div>'),
                minWidth: "110px",
                sort: {
                    compare: (a, b) => compareModul(a, b),
                },
                formatter: (cell) => html(modulBadgeHtml(cell)),
            },
            {
                id: "islem",
                name: html('<div style="margin-top:3px;">İşlem</div>'),
                minWidth: "165px",
                sort: {
                    compare: (a, b) => compareIslem(a, b),
                },
                formatter: (cell) => html(islemBadgeHtml(cell)),
            },
            {
                id: "detay",
                name: html('<div style="margin-top:3px;text-align:center;">Detay</div>'),
                sort: false,
                formatter: (cell, row) => {
                    if (!cell && cell !== 0) return html('<span style="color:#9ca3af">—</span>');
                    const tarih  = row?.cells?.[0]?.data ?? "";
                    const yapan  = row?.cells?.[1]?.data ?? "";
                    const modul  = row?.cells?.[2]?.data ?? "";
                    const islem  = row?.cells?.[3]?.data ?? "";
                    const snapshotStr = typeof cell === "string" ? cell : JSON.stringify(cell);
                    return html(
                        `<button class="log-detay-btn"
                            data-snapshot="${encodeURIComponent(snapshotStr)}"
                            data-modul="${esc(modul)}"
                            data-islem="${esc(islem)}"
                            data-yapan="${encodeURIComponent(yapan)}"
                            data-tarih="${encodeURIComponent(tarih)}"
                            title="Detayı Göster">
                            <span class="material-icons">info_outline</span>
                        </button>`
                    );
                },
            },
        ],

        server: {
            url,
            then: (data) => {
                const total = data.total ?? (data.items?.length ?? 0);
                const countEl = document.getElementById("log-count");
                if (countEl) countEl.textContent = `${total} kayıt`;
                return (data.items || []).map((row) => [
                    row.olusturma_tarihi
                        ? new Date(row.olusturma_tarihi).toLocaleString("tr-TR")
                        : "—",
                    `${row.first_name ?? ""} ${row.last_name ?? ""}`.trim() || "—",
                    row.modul ?? "",
                    row.islem ?? "",
                    row.snapshot ?? null,
                ]);
            },
        },

        autoWidth: false,
        search: false,
        sort: true,
        pagination: { limit: uiState.log.paginationLimit },

        style: {
            container: { padding: "0px" },
            footer: { "border-radius": "0px", "box-shadow": "none" },
            header: { padding: "10px 20px" },
            table: { "white-space": "nowrap", "font-size": "11px", "margin-bottom": "3px" },
            th: { padding: "8px 16px" },
            td: { padding: "10px 16px" },
        },
    }).render(tableGrid);

    initLogGridFooter();
}

function initLogGridFooter() {
    requestAnimationFrame(() => {
        const pagination = document.querySelector(".log-table-grid .gridjs-pagination");
        if (!pagination) return;
        if (pagination.querySelector(".log-per-page-container")) return;

        const perPageContainer = document.createElement("div");
        perPageContainer.classList.add("per-page-container", "log-per-page-container");

        const label = document.createElement("span");
        label.textContent = "Sayfa başı:";

        const select = document.createElement("select");
        select.classList.add("per-page", "log-per-page");

        ["10", "25", "50", "100"].forEach((val) => {
            const opt = document.createElement("option");
            opt.value = val;
            opt.textContent = val;
            if (val === String(uiState.log.paginationLimit)) opt.selected = true;
            select.appendChild(opt);
        });

        perPageContainer.appendChild(label);
        perPageContainer.appendChild(select);
        pagination.appendChild(perPageContainer);
    });
}

function rerenderLogGridWithLastUrl() {
    if (!uiState.log.lastSearchUrl) return;
    renderLogGrid(uiState.log.lastSearchUrl);
}

// ── Detail Dialog ────────────────────────────────────────────────────────────

function buildDetailRowsHtml(snapshot, modul) {
    if (!snapshot) return '<div class="log-detail-empty">Detay bilgisi yok</div>';

    const fields = MODULE_FIELDS[modul] ?? [];
    const usedKeys = new Set();
    let rows = "";

    fields.forEach(({ key, combined, label, fmt, aliases = [] }) => {
        let value;
        if (combined) {
            combined.forEach((k) => usedKeys.add(k));
            value = fmt ? fmt(snapshot) : combined.map((k) => snapshot[k]).filter(Boolean).join(" / ") || null;
        } else {
            usedKeys.add(key);
            aliases.forEach((alias) => usedKeys.add(alias));
            const lookupKeys = [key, ...aliases];
            const matchedKey = lookupKeys.find((candidate) => snapshot[candidate] !== undefined && snapshot[candidate] !== null && snapshot[candidate] !== "");
            const raw = matchedKey ? snapshot[matchedKey] : snapshot[key];
            value = fmt ? fmt(raw) : (raw ?? null);
        }
        if (value === null || value === undefined || value === "") return;
        rows += `<div class="log-detail-row">
            <span class="log-detail-label">${esc(label)}</span>
            <span class="log-detail-value" title="${esc(value)}">${esc(value)}</span>
        </div>`;
    });

    // Bilinmeyen kalan alanlar
    Object.entries(snapshot).forEach(([k, v]) => {
        if (usedKeys.has(k)) return;
        if (v === null || v === undefined || v === "") return;
        const label = k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        const displayVal = typeof v === "object" ? JSON.stringify(v) : v;
        rows += `<div class="log-detail-row">
            <span class="log-detail-label">${esc(label)}</span>
            <span class="log-detail-value" title="${esc(displayVal)}">${esc(displayVal)}</span>
        </div>`;
    });

    return rows || '<div class="log-detail-empty">Detay bilgisi yok</div>';
}

function openLogDetailDialog(btn) {
    const dialog = document.getElementById("logDetailDialog");
    if (!dialog) return;

    const modul  = btn.dataset.modul ?? "";
    const islem  = btn.dataset.islem ?? "";
    const yapan  = decodeURIComponent(btn.dataset.yapan ?? "");
    const tarih  = decodeURIComponent(btn.dataset.tarih ?? "");

    let snapshot = null;
    try {
        snapshot = JSON.parse(decodeURIComponent(btn.dataset.snapshot ?? ""));
    } catch { /* snapshot null kalır */ }

    const modulLabel = MODUL_LABEL[modul] ?? modul ?? "—";
    const islemCfg   = ISLEM_BADGE[islem] ?? { bg: "#f3f4f6", color: "#374151", border: "#d1d5db", label: islem };
    const banner     = ISLEM_BANNER[islem];
    const sectionTitle = MODULE_SECTION[modul] ?? "Kayıt Bilgileri";
    const rowsHtml   = buildDetailRowsHtml(snapshot, modul);

    dialog.innerHTML = `
<div class="log-detail-container">
        <div class="log-detail-header">
        <div class="log-detail-header-left">
            <span class="log-detail-title">Kayıt Detayı</span>
            <span class="log-badge log-badge--modul" title="${esc(modulLabel)}">${esc(modulLabel)}</span>
            <span class="log-badge" title="${esc(islemCfg.label)}" style="background:${islemCfg.bg};color:${islemCfg.color};border-color:${islemCfg.border}">${esc(islemCfg.label)}</span>
        </div>
        <button class="log-detail-close-btn" title="Kapat">
            <span class="material-icons">close</span>
        </button>
    </div>
    <div class="log-detail-meta">
        <div class="log-detail-meta-item">
            <span class="log-detail-meta-label">YAPAN</span>
            <span class="log-detail-meta-value" title="${esc(yapan || "—")}">${esc(yapan || "—")}</span>
        </div>
        <div class="log-detail-meta-item">
            <span class="log-detail-meta-label">ZAMAN</span>
            <span class="log-detail-meta-value" title="${esc(tarih || "—")}">${esc(tarih || "—")}</span>
        </div>
    </div>
    ${banner ? `<div class="log-detail-banner" style="background:${banner.bg};color:${banner.color};border-left-color:${banner.border}">
        <span class="material-icons" style="font-size:16px">${banner.icon}</span>
        <span>${esc(banner.text)}</span>
    </div>` : ""}
    <div class="log-detail-body">
        <div class="log-detail-section-title">${esc(sectionTitle)}</div>
        <div class="log-detail-rows">${rowsHtml}</div>
    </div>
    <div class="log-detail-footer">
        <button class="log-detail-kapat log-detail-close-btn">Kapat</button>
    </div>
</div>`;

    dialog.showModal();
}

// ── Page lifecycle ───────────────────────────────────────────────────────────

export function clearLogGrid() {
    if (logGrid) {
        logGrid.destroy();
        logGrid = null;
    }
    const tableGrid = document.querySelector(".log-table-grid");
    if (tableGrid) tableGrid.innerHTML = "";
    const countEl = document.getElementById("log-count");
    if (countEl) countEl.textContent = "";
    uiState.log.paginationLimit = 10;
    uiState.log.offset = 0;
    uiState.log.lastSearchUrl = null;
}

function resetLogFilters() {
    const modulSel = document.querySelector("#log-modul-filter");
    const islemSel = document.querySelector("#log-islem-filter");
    const yapanSel = document.querySelector("#log-yapan-filter");
    const tarihBaslangicInput = document.querySelector("#log-tarih-baslangic");
    const tarihBitisInput = document.querySelector("#log-tarih-bitis");

    if (modulSel) modulSel.value = "";
    if (islemSel) islemSel.value = "";
    if (yapanSel) yapanSel.value = "";
    if (tarihBaslangicInput) { tarihBaslangicInput.value = ""; tarihBaslangicInput.max = ""; }
    if (tarihBitisInput) { tarihBitisInput.value = ""; tarihBitisInput.min = ""; }

    uiState.log.filters.modul = "";
    uiState.log.filters.islem = "";
    uiState.log.filters.yapan_tc = "";
    uiState.log.filters.tarih_baslangic = "";
    uiState.log.filters.tarih_bitis = "";

    clearLogGrid();
    syncLogFilterBadge();
}

export function resetLogPage() {
    resetLogFilters();
    document.getElementById("logDetailDialog")?.close();
}

export function renderLogPage() {
    ensureLogView();

    const sectionBody = document.querySelector("#log-filter-section .section-body");
    const searchBtn = document.querySelector(".log-search");
    const resetBtn  = document.querySelector(".log-filter-reset");
    const modulSel  = document.querySelector("#log-modul-filter");
    const islemSel  = document.querySelector("#log-islem-filter");
    const yapanSel  = document.querySelector("#log-yapan-filter");
    const tarihBaslangicInput = document.querySelector("#log-tarih-baslangic");
    const tarihBitisInput     = document.querySelector("#log-tarih-bitis");
    const yapanLabel = document.querySelector('label[for="log-yapan-filter"]');
    const exportBtn = document.querySelector(".log-demirbas-export-btn");

    if (modulSel) modulSel.value = uiState.log.filters.modul;
    if (islemSel) islemSel.value = uiState.log.filters.islem;
    if (yapanSel) yapanSel.value = uiState.log.filters.yapan_tc;
    if (yapanLabel) yapanLabel.textContent = "İşlemi Gerçekleştiren";
    if (tarihBaslangicInput) tarihBaslangicInput.value = uiState.log.filters.tarih_baslangic;
    if (tarihBitisInput) tarihBitisInput.value = uiState.log.filters.tarih_bitis;
    if (tarihBaslangicInput) tarihBaslangicInput.max = uiState.log.filters.tarih_bitis || "";
    if (tarihBitisInput) tarihBitisInput.min = uiState.log.filters.tarih_baslangic || "";
    syncLogFilterBadge();
    if (exportBtn) exportBtn.hidden = uiState.currentUser?.yetki !== "admin";

    if (uiState.log.lastSearchUrl) renderLogGrid(uiState.log.lastSearchUrl);
    else clearLogGrid();

    if (sectionBody) {
        withFilterOverlay(sectionBody, () => loadLogKisilerDropdown()).catch((err) => {
            console.error("❌ Log filtre dropdown init hatası:", err);
        });
    } else {
        loadLogKisilerDropdown().catch((err) => {
            console.error("❌ Log filtre dropdown init hatası:", err);
        });
    }

    if (logDidBound) return;
    logDidBound = true;

    modulSel?.addEventListener("change", () => { uiState.log.filters.modul = modulSel.value; });
    islemSel?.addEventListener("change", () => { uiState.log.filters.islem = islemSel.value; });
    yapanSel?.addEventListener("change", () => { uiState.log.filters.yapan_tc = yapanSel.value; });
    tarihBaslangicInput?.addEventListener("change", () => {
        if (tarihBitisInput?.value && tarihBaslangicInput.value > tarihBitisInput.value) {
            tarihBaslangicInput.value = "";
            uiState.log.filters.tarih_baslangic = "";
            return;
        }
        uiState.log.filters.tarih_baslangic = tarihBaslangicInput.value;
        if (tarihBitisInput) tarihBitisInput.min = tarihBaslangicInput.value || "";
    });
    tarihBitisInput?.addEventListener("change", () => {
        if (tarihBaslangicInput?.value && tarihBitisInput.value < tarihBaslangicInput.value) {
            tarihBitisInput.value = "";
            uiState.log.filters.tarih_bitis = "";
            return;
        }
        uiState.log.filters.tarih_bitis = tarihBitisInput.value;
        if (tarihBaslangicInput) tarihBaslangicInput.max = tarihBitisInput.value || "";
    });

    document.body.addEventListener("change", (e) => {
        if (e.target?.matches(".log-per-page")) {
            uiState.log.paginationLimit = Number(e.target.value);
            rerenderLogGridWithLastUrl();
        }
    });

    searchBtn?.addEventListener("click", () => {
        renderLogGrid();
        syncLogFilterBadge();
    });

    resetBtn?.addEventListener("click", () => {
        resetLogFilters();
    });

    document.body.addEventListener("click", (e) => {
        const exportCsvBtn = e.target.closest(".log-demirbas-export-btn");
        if (exportCsvBtn) {
            downloadDemirbasCsvExport(exportCsvBtn);
            return;
        }

        const detayBtn = e.target.closest(".log-detay-btn");
        if (detayBtn) { openLogDetailDialog(detayBtn); return; }

        if (e.target.closest(".log-detail-close-btn")) {
            document.getElementById("logDetailDialog")?.close();
        }
    });

    document.querySelectorAll("#log-view .section-collapse-btn").forEach((btn) => {
        btn.addEventListener("click", () => btn.closest(".page-section").classList.toggle("collapsed"));
    });
}

function ensureLogView() {
    if (document.getElementById("log-view")) return;
    document.getElementById("page-content").insertAdjacentHTML("beforeend", `
<div id="log-view">
    <div class="page-section" id="log-filter-section">
        <div class="section-header">
            <div class="section-header-left">
                <span class="material-icons section-icon">filter_list</span>
                <span class="section-title">Filtreler</span>
                <span class="filter-aktif-badge" hidden>
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                    </svg>
                    Filtre aktif
                </span>
            </div>
            <div class="section-header-right">
                <button class="log-collapse-btn section-collapse-btn" title="Daralt/Genişlet">
                    <span class="material-icons">expand_less</span>
                </button>
            </div>
        </div>
        <div class="section-body">
            <div class="kisiler-filter-row">
                <div class="column-container">
                    <label for="log-modul-filter">Modül</label>
                    <select id="log-modul-filter">
                        <option value="">Tümü</option>
                        <option value="demirbas">Demirbaş</option>
                        <option value="tanim">Tanım</option>
                        <option value="kisi">Kişi</option>
                        <option value="uye">Üye</option>
                    </select>
                </div>
                <div class="column-container">
                    <label for="log-islem-filter">İşlem</label>
                    <select id="log-islem-filter">
                        <option value="">Tümü</option>
                        <option value="olusturma">Oluşturma</option>
                        <option value="duzenleme">Düzenleme</option>
                        <option value="silme">Silme</option>
                        <option value="durum_degisikligi">Durum Değişikliği</option>
                        <option value="yeni_sifre">Yeni Şifre</option>
                    </select>
                </div>
                <div class="column-container">
                    <label for="log-yapan-filter">Yapan Kişi</label>
                    <select id="log-yapan-filter">
                        <option value="">Tümü</option>
                    </select>
                </div>
                <div class="column-container">
                    <label for="log-tarih-baslangic">Başlangıç Tarihi</label>
                    <input type="date" id="log-tarih-baslangic" />
                </div>
                <div class="column-container">
                    <label for="log-tarih-bitis">Bitiş Tarihi</label>
                    <input type="date" id="log-tarih-bitis" />
                </div>
                <div class="filter-button-container">
                    <button class="log-filter-reset">Temizle</button>
                    <button class="log-search">Ara</button>
                </div>
            </div>
        </div>
    </div>

    <div class="page-section" id="log-grid-section">
        <div class="section-header">
            <div class="section-header-left">
                <span class="section-title">İşlem Kayıtları</span>
                <span class="section-count" id="log-count"></span>
            </div>
            <div class="section-header-right">
                <button type="button" class="log-demirbas-export-btn btn-header-action" hidden>
                    <span class="material-icons">download</span>
                    <span>Demirba\u015f CSV Export</span>
                </button>
            </div>
        </div>
        <div class="section-body">
            <div class="log-table-grid"></div>
        </div>
    </div>

    <dialog id="logDetailDialog"></dialog>
</div>`);
}

async function loadLogKisilerDropdown() {
    const selectEl = document.querySelector("#log-yapan-filter");
    if (!selectEl) return;

    const currentValue = uiState.log.filters.yapan_tc || "";
    while (selectEl.options.length > 1) selectEl.remove(1);

    try {
        const response = await fetch(`${API}/kisiler-dropdown?durum=tumu`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();

        (data.items || []).forEach((item) => {
            const fullLabel = String(item.ad_soyad ?? "");
            const opt = document.createElement("option");
            opt.value = item.tc;
            opt.textContent = truncateLabel(fullLabel, 32);
            opt.title = fullLabel;
            selectEl.appendChild(opt);
        });

        selectEl.value = currentValue;
        const selected = selectEl.options?.[selectEl.selectedIndex];
        selectEl.title = selected?.title?.trim?.() || selected?.textContent?.trim() || "";
    } catch (err) {
        console.error("❌ /kisiler-dropdown yüklenemedi:", err);
    }
}
